import axios from "./config";
import { getHeader } from "../utility/index";

// Interface for coordinate
export interface Coordinate {
  lat: number;
  lng: number;
}

// Interface for creating a territory (POST request)
export interface CreateTerritoryPayload {
  comment: string;
  coordinates: Coordinate[];
  territory_name: string;
}

// Interface for updating a territory (PUT request)
export interface UpdateTerritoryPayload {
  territory_name: string;
  coordinates: Coordinate[];
  comment: string;
  status_active?: boolean;
}

// Interface for territory response
export interface TerritoryResponse {
  id?: string | number;
  territory_name: string;
  coordinates: Coordinate[];
  comment: string;
  [key: string]: any;
}

// Interface for list response
export interface TerritoryListResponse {
  data?: TerritoryResponse[];
  items?: TerritoryResponse[];
  results?: TerritoryResponse[];
  territories?: TerritoryResponse[];
  total?: number;
  page?: number;
  size?: number;
  [key: string]: any;
}

// Interface for paginated response
export interface PaginatedTerritoryResponse {
  territories: TerritoryResponse[];
  total: number;
  page: number;
  size: number;
}

/**
 * Get all LMD territories with pagination support
 * GET: /admin/lmd-territories
 */
export const getAllLmdTerritories = async (
  salesOfficeId?: string,
  activeStatus?: boolean,
  page?: number,
  size?: number,
  /** Server-side search across territories (not limited to current page). */
  search?: string
): Promise<TerritoryResponse[] | PaginatedTerritoryResponse> => {
  let url = `/admin/lmd-territories`;
  const params: string[] = [];

  if (salesOfficeId) {
    params.push(`sales_office_id=${salesOfficeId}`);
  }

  if (activeStatus !== undefined) {
    params.push(`status_active=${activeStatus}`);
  }

  if (page !== undefined) {
    params.push(`page=${page}`);
  }

  if (size !== undefined) {
    params.push(`size=${size}`);
  }

  if (search !== undefined && search.trim() !== "") {
    params.push(`search=${encodeURIComponent(search.trim())}`);
  }
  
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  
  const response = await axios.get<TerritoryListResponse>(url, {
    headers: getHeader(),
  });
  
  // Log the raw response for debugging
  console.log("Raw API response:", response.data);
  
  // Handle different response structures
  const data = response.data;
  
  // If pagination parameters were provided, return paginated response
  if (page !== undefined || size !== undefined) {
    let territories: TerritoryResponse[] = [];
    let total = 0;
    let currentPage = page || 1;
    let pageSize = size || 20;
    
    // Check if territories exists and is an array
    if (data?.territories && Array.isArray(data.territories)) {
      territories = data.territories;
      total = data.total || data.territories.length;
      currentPage = data.page || currentPage;
      pageSize = data.size || pageSize;
    } else if (data?.items && Array.isArray(data.items)) {
      territories = data.items;
      total = data.total || data.items.length;
      currentPage = data.page || currentPage;
      pageSize = data.size || pageSize;
    } else if (data?.data && Array.isArray(data.data)) {
      territories = data.data;
      total = data.total || data.data.length;
      currentPage = data.page || currentPage;
      pageSize = data.size || pageSize;
    } else if (data?.results && Array.isArray(data.results)) {
      territories = data.results;
      total = data.total || data.results.length;
      currentPage = data.page || currentPage;
      pageSize = data.size || pageSize;
    } else if (Array.isArray(data)) {
      territories = data;
      total = data.length;
    }
    
    return {
      territories,
      total,
      page: currentPage,
      size: pageSize,
    };
  }
  
  // Legacy behavior: return array if no pagination params
  // Check if territories exists and is an array
  if (data?.territories && Array.isArray(data.territories)) {
    console.log("Returning", data.territories.length, "territories from data.territories");
    return data.territories;
  }
  
  // Fallback: try other possible structures
  if (Array.isArray(data)) {
    console.log("Response is direct array, returning", data.length, "territories");
    return data;
  } else if (data?.data && Array.isArray(data.data)) {
    console.log("Response has data.data array, returning", data.data.length, "territories");
    return data.data;
  } else if (data?.items && Array.isArray(data.items)) {
    console.log("Response has data.items array, returning", data.items.length, "territories");
    return data.items;
  } else if (data?.results && Array.isArray(data.results)) {
    console.log("Response has data.results array, returning", data.results.length, "territories");
    return data.results;
  }
  
  console.warn("Unexpected response structure:", data);
  console.warn("Response keys:", Object.keys(data || {}));
  return [];
};

/**
 * Create a new LMD territory
 * POST: /web/api/v1/admin/lmd-territories
 */
export const createLmdTerritory = async (
  payload: CreateTerritoryPayload
): Promise<TerritoryResponse> => {
  const url = `/admin/lmd-territories`;
  const response = await axios.post<TerritoryResponse>(url, payload, {
    headers: getHeader(),
  });
  return response.data;
};

/**
 * Get a specific LMD territory by ID
 * GET: /web/api/v1/admin/lmd-territories/{territory_id}
 */
export const getLmdTerritory = async (
  territoryId: string | number
): Promise<TerritoryResponse> => {
  const url = `/admin/lmd-territories/${territoryId}`;
  const response = await axios.get<TerritoryResponse>(url, {
    headers: getHeader(),
  });
  return response.data;
};

/**
 * Update an existing LMD territory
 * PUT: /web/api/v1/admin/lmd-territories/{territory_id}
 */
export const updateLmdTerritory = async (
  territoryId: string | number,
  payload: UpdateTerritoryPayload
): Promise<TerritoryResponse> => {
  const url = `/admin/lmd-territories/${territoryId}`;
  const response = await axios.put<TerritoryResponse>(url, payload, {
    headers: getHeader(),
  });
  return response.data;
};

/**
 * Get LMD territories by sales office (for map boundaries only)
 * GET: /admin/lmd-territories/by-sales-office/{sales_office_id}
 * @param statusActive - optional: true = active only, false = inactive only, undefined = all territories
 */
export const getLmdTerritoriesBySalesOffice = async (
  salesOfficeId: string,
  statusActive?: boolean
): Promise<TerritoryResponse[]> => {
  let url = `/admin/lmd-territories/by-sales-office/${salesOfficeId}`;
  if (statusActive !== undefined) {
    url += `?status_active=${statusActive}`;
  }
  const response = await axios.get<TerritoryListResponse>(url, {
    headers: getHeader(),
  });
  const data = response.data;
  if (data?.territories && Array.isArray(data.territories)) {
    return data.territories;
  }
  if (data?.data && Array.isArray(data.data)) {
    return data.data;
  }
  if (data?.items && Array.isArray(data.items)) {
    return data.items;
  }
  if (data?.results && Array.isArray(data.results)) {
    return data.results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

/**
 * Delete an LMD territory
 * DELETE: /admin/lmd-territories/{territory_id}
 */
export const deleteLmdTerritory = async (
  territoryId: string | number
): Promise<void> => {
  const url = `/admin/lmd-territories/${territoryId}`;
  await axios.delete(url, {
    headers: getHeader(),
  });
};

